import { logoutAuth as logoutApi, verifyOtpAuth as verifyOtp } from '@api/generated/sdk.gen';
import {
  clearTokens,
  getAccess,
  getBackendDeviceId,
  getClientDeviceId,
  getRefresh,
  isAccessExpired,
  setAccess,
  setBackendDeviceId,
  setRefresh,
} from '@features/auth';
import { Permissions } from '@shared/constants/permissions';
import { parseApiError } from '@shared/lib/api-error';

import type { UserResponse } from '@api/generated/types.gen';

import { loadIdentity, resetIdentityCache } from './identity-cache';

import type { AuthProvider } from '@refinedev/core';

/**
 * Параметры `login` для Refine-провайдера.
 *
 * Шаг 1 (LoginPage) не требует вызова провайдера: он сам шлёт `/otp/request`
 * и навигирует на verify-страницу. Шаг 2 (VerifyOtpPage) вызывает `login`
 * с этим объектом — провайдер делает `/otp/verify` и сохраняет токены.
 */
type AuthLoginParams = {
  phone: string;
  code: string;
  deviceName: string;
};

/**
 * Проверяет, что у текущего пользователя есть право `admin.access`.
 * Без него вход в админку запрещён, независимо от других прав.
 */
function hasAdminAccess(me: UserResponse | null): boolean {
  return Boolean(me?.permissions?.includes(Permissions.ADMIN_ACCESS));
}

/**
 * Best-effort вызов `/api/auth/logout`. Ошибка сети не должна мешать локальной
 * очистке токенов, поэтому исключения глотаем — клиент в любом случае чистится
 * вызывающей стороной.
 */
async function revokeServerSession(refreshToken: string, deviceId: string): Promise<void> {
  try {
    await logoutApi({ body: { refreshToken, deviceId } });
  } catch {
    return;
  }
}

/**
 * Аутентификационный провайдер Refine.
 *
 * Authentication flow:
 * 1. LoginPage сам зовёт `/api/auth/otp/request` и навигирует на `/verify-otp`.
 * 2. VerifyOtpPage вызывает `authProvider.login({ phone, code, deviceName })`
 *    → провайдер шлёт `/api/auth/otp/verify`, сохраняет токены, **проверяет
 *    `admin.access`**, при отсутствии — чистит токены и возвращает ошибку.
 * 3. `check` подтверждает наличие access (или пробует refresh), плюс что
 *    у пользователя сохранилось право `admin.access`.
 * 4. `logout` шлёт `/api/auth/logout`, чистит локальные токены и кэш профиля.
 *
 * Permissions для UI-гейтинга берутся из `UserResponse.permissions[]`,
 * который бэк отдаёт в `/api/users/me`. Источник истины для авторизации —
 * по-прежнему сервер; клиентский гейтинг — только UX.
 */
export const authProvider: AuthProvider = {
  login: async (params: AuthLoginParams) => {
    try {
      const res = await verifyOtp({
        body: {
          phone: params.phone,
          code: params.code,
          deviceId: getClientDeviceId(),
          deviceName: params.deviceName,
          platform: 'ANDROID',
        },
      });
      const data = res.data;
      if (
        !data?.accessToken ||
        !data.refreshToken ||
        !data.deviceId ||
        data.accessExpiresInSeconds == null
      ) {
        return {
          success: false,
          error: { name: 'AuthError', message: 'INVALID_RESPONSE' },
        };
      }
      setAccess(data.accessToken, data.accessExpiresInSeconds);
      setRefresh(data.refreshToken);
      setBackendDeviceId(data.deviceId);
      resetIdentityCache();

      const me = await loadIdentity();
      if (!hasAdminAccess(me)) {
        await revokeServerSession(data.refreshToken, data.deviceId);
        clearTokens();
        resetIdentityCache();
        return {
          success: false,
          error: { name: 'AuthError', message: 'NO_ADMIN_ACCESS' },
        };
      }

      return { success: true, redirectTo: '/' };
    } catch (err: unknown) {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      return {
        success: false,
        error: { name: 'AuthError', message: apiErr.code },
      };
    }
  },

  logout: async () => {
    const refresh = getRefresh();
    const backendDeviceId = getBackendDeviceId();
    if (refresh && backendDeviceId) {
      await revokeServerSession(refresh, backendDeviceId);
    }
    clearTokens();
    resetIdentityCache();
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    const hasSomeToken = (getAccess() !== null && !isAccessExpired()) || getRefresh() !== null;
    if (!hasSomeToken) {
      return { authenticated: false, redirectTo: '/login' };
    }
    const me = await loadIdentity();
    if (!hasAdminAccess(me)) {
      clearTokens();
      resetIdentityCache();
      return { authenticated: false, redirectTo: '/login', logout: true };
    }
    return { authenticated: true };
  },

  getIdentity: () => loadIdentity(),

  getPermissions: async () => {
    const me = await loadIdentity();
    return me?.permissions ?? [];
  },

  onError: (error: unknown) => {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 401) {
      return Promise.resolve({ logout: true, redirectTo: '/login' });
    }
    if (status === 403) {
      return Promise.resolve({ logout: false });
    }
    return Promise.resolve({});
  },
};
